import { Component, OnInit, AfterViewInit, ElementRef, HostListener, ViewChild } from '@angular/core';

import { RouterModule } from '@angular/router';
import { GetScholarshipRequest, ScholarshipType } from '../../../models/scholarships/getScholarshipRequest';
import { ScholarshipsService } from '../../../services/scholarships/scholarships.service';
import { ScholarshipResponse } from '../../../models/scholarships/scholarshipResponse';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { InputTextModule } from 'primeng/inputtext';

import { DeadLineDetails } from '../../../models/scholarships/addScholarshipRequest';

@Component({
  selector: 'app-summer-programs',
  standalone: true,
  imports: [RouterModule, ButtonModule, DropdownModule, CommonModule, 
      FormsModule, ReactiveFormsModule, AutoCompleteModule, InputTextModule],
  templateUrl: './summer-programs.component.html',
  styleUrl: './summer-programs.component.scss'
})
export class SummerProgramsComponent implements OnInit, AfterViewInit{

  constructor(private scholarshipsService: ScholarshipsService, private messageService: MessageService,
    private formBuilder: FormBuilder
   ){}

  readonly pageType = ScholarshipType.SummerSchoolIndia;
  streamOptions: string[] = [];
  levelOptions: string[] = [];
  schoplarshipNames: string[] = [];
  scholarshipsList: ScholarshipResponse[] = [];
  loadingScholarshipId?: number | null;

  filterForm!: FormGroup;
  @ViewChild('streamDropdown', { read: ElementRef }) streamDropdownRef!: ElementRef;
  streamDropdownWidth = 0;

  isLoading = false;
  submitted = false;

  ngOnInit(): void {
    this.initializeForm();
    this.getStreamOrLevelOptions();
    this.getScholarships();
  }

  ngAfterViewInit(): void {
    // measure after view init
    setTimeout(() => this.setStreamDropdownWidth(), 50);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.setStreamDropdownWidth();
  }

  private setStreamDropdownWidth(): void {
    try {
      const el = this.streamDropdownRef?.nativeElement as HTMLElement | undefined;
      if (el) {
        const w = el.getBoundingClientRect().width || el.offsetWidth || 0;
        this.streamDropdownWidth = Math.max(0, Math.round(w));
      }
    } catch (err) {
      // ignore
    }
  }

  myScholarshipsOnly = false;
  get filteredScholarships() {
    return this.myScholarshipsOnly 
      ? this.scholarshipsList.filter(s => s.isMyScholarship) 
      : this.scholarshipsList;
  }
  get myScholarshipCount(): number {
    return this.scholarshipsList.filter(s => s.isMyScholarship).length;
  }

  getStreamOrLevelOptions(){
    this.scholarshipsService.getStream(this.pageType).subscribe({
      next: (response: any) => {
        this.streamOptions = response.result;
      },
      error: (err: any) => {
        console.warn('Error fetching stream options:' , err)
      }
    })
    this.scholarshipsService.getLevel(this.pageType).subscribe({
      next: (response: any) => {
        this.levelOptions = response.result;
      },
      error: (err: any) => {
        console.warn('Error fetching level options:' , err)
      }
    })
  }

  filterScholarshipNames(event: AutoCompleteCompleteEvent){
    if(event.query.length > 3){
      this.scholarshipsService.listScholarShipsName_ByInitials(event.query,this.pageType).subscribe({
        next: (response) => {
          this.schoplarshipNames = response.result;
        }
      });
    }else{
      this.schoplarshipNames = [];
    }
  }

  initializeForm(){
    this.filterForm = this.formBuilder.group({
      scholarshipName: [''],
      stream: [null],
      level: [null]
    });
  }

  getScholarships(){
    this.isLoading = true;
    const { scholarshipName, stream, level } = this.filterForm.value;
const req: GetScholarshipRequest = {
  scholarshipName: scholarshipName || null,
  scholarshipType: this.pageType,
  streams: stream ? [stream] : null,
  levels: level ? [level] : null,
};
    this.scholarshipsService.getScholarships(req).subscribe({
      next: (response) => {
        this.scholarshipsList = response
        ? response.result.map(item => ({
            ...item,
            isMyScholarship: item.isMyScholarship || false   // ✅ default false
          }))
        : [];
        this.isLoading = false; 
        this.getMyScholarshipsList();
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: `Error Fetching Scholarships: ${err}` });
      }
    })
  }

  getMyScholarshipsList() {
    this.scholarshipsService.getMyScholarships().subscribe({
      next: (response) => {
        const myScholarshipsIds = response.result.map(s => s.scholarship.id);
  
        // ✅ Always merge with base list and set correct flags
        this.scholarshipsList = this.scholarshipsList.map(item => ({
          ...item,
          isMyScholarship: myScholarshipsIds.includes(item.id)
        }));
      },
      error: (err) => {
        console.warn('Error fetching my scholarships:', err);
        // ✅ Ensure no stale flags remain on error
        this.scholarshipsList = this.scholarshipsList.map(item => ({
          ...item,
          isMyScholarship: false
        }));
      }
    });
  }

  formatDeadline(d?: DeadLineDetails | null): string {
  if (!d) return 'N/A';
  const isEmpty = (v?: string | null) => !v || !v.trim() || v.trim().toUpperCase() === 'NA';
  if (!isEmpty(d.exactDate)) return d.exactDate!.trim();
  if (!isEmpty(d.month)) return d.month!.trim();
  return 'N/A';
}

  toggleMyScholarship(id: number){
    const index = this.scholarshipsList.findIndex(s => s.id === id);
    if(index === -1) return;
  
    const current = this.scholarshipsList[index];
    const newValue = !current.isMyScholarship; // what user clicked
  
    //  Optimistically update UI
    this.scholarshipsList = this.scholarshipsList.map(item =>
      item.id === id ? { ...current, isMyScholarship: newValue } : item
    );
  
    // Show spinner
    this.loadingScholarshipId = id;
  
    if(newValue){
      this.scholarshipsService.addMyScholarship(id).subscribe({
        next: () => {
          this.loadingScholarshipId = null;
        },
        error: (err) => {
          console.warn('Error adding scholarship:', err);
          // Rollback on failure
          this.scholarshipsList = this.scholarshipsList.map(item =>
            item.id === id ? { ...current, isMyScholarship: false } : item
          );
          this.loadingScholarshipId = null;
        }
      });
    } else {
      this.scholarshipsService.deleteMyScholarShip(id).subscribe({
        next: () => {
          this.loadingScholarshipId = null;
        },
        error: (err) => {
          console.warn('Error removing scholarship:', err);
          this.scholarshipsList = this.scholarshipsList.map(item =>
            item.id === id ? { ...current, isMyScholarship: true } : item
          );
          this.loadingScholarshipId = null;
        }
      });
    }
  }
}
